import { Moment } from 'moment-timezone';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { convertDaycareAvailabilityDate } from 'actions/availability/AvailabilityDataConverter';
import { DaycareAvailabilityDateDto } from 'actions/availability/AvailabilityDtos';
import {
    fetchStaffsAvailabilityThunk,
    getStaffNextAvailableDateThunk
} from 'actions/availability/AvailabilityActions';
import { ApiPublicClient } from 'api/ApiClient';
import {
    ONLINE_BOOKING_CLEAR_BOARDING_AVAILABILITY,
    ONLINE_BOOKING_CLEAR_DAYCARE_AVAILABILITY,
    ONLINE_BOOKING_ERROR,
    ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_START,
    ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_START,
    ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_START,
    ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_SUCCESS
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { DaycareAvailabilityDate, GroomingAvailableDate } from 'types/Availability';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    isOnlineBookingGroomingItem
} from 'types/OnlineBooking';
import { getServiceDurationByPet } from 'utils/serviceUtils';

const daycareAvailabilityUrl = 'room_availability';
const boardingAvailabilityUrl = 'room_availability';

export const clearOnlineBookingDaycareAvailability = () => ({
    type: ONLINE_BOOKING_CLEAR_DAYCARE_AVAILABILITY
});

const createDaycareAvailabilityRequestKey = (
    serviceId: number,
    petIds: Array<number>,
    date: Moment
) => {
    return `${serviceId}:${petIds.sort((a, b) => a - b).join(',')}:${date.format('YYYY-MM-DD')}`;
};

const buildEmptyDaycareAvailability = (date: Moment): DaycareAvailabilityDate => ({
    date: date.clone(),
    dayName: date.format('dddd').toLowerCase(),
    reason: 'no_available_slots',
    dropOffSlots: []
});

export const clearOnlineBookingBoardingAvailability = () => ({
    type: ONLINE_BOOKING_CLEAR_BOARDING_AVAILABILITY
});

const createBoardingAvailabilityRequestKey = (
    serviceId: number,
    petIds: Array<number>,
    startDate: Moment,
    endDate: Moment
) => {
    const sortedPetIds = petIds.sort((a, b) => a - b).join(',');
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');

    return `${serviceId}:${sortedPetIds}:${start}:${end}`;
};

const buildEmptyBoardingAvailability = (startDate: Moment): DaycareAvailabilityDate => ({
    date: startDate.clone(),
    dayName: startDate.format('dddd').toLowerCase(),
    reason: 'no_available_slots',
    dropOffSlots: []
});

export const getOnlineBookingBoardingAvailabilityThunk = (
    startDate: Moment,
    endDate: Moment
): ThunkAction<Promise<DaycareAvailabilityDate>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const state = getState();
        const { marketplace, onlineBooking } = state;
        const boardingBookingItems = onlineBooking.bookingItems.filter(isOnlineBookingBoardingItem);
        const sharedBoardingService = boardingBookingItems.find(item => item.service)?.service;
        const petIds = boardingBookingItems.map(item => item.pet.id).filter(Boolean);

        if (!sharedBoardingService || petIds.length === 0) {
            dispatch(clearOnlineBookingBoardingAvailability());

            throw new Error('Boarding availability requires a selected service and pets');
        }

        const requestKey = createBoardingAvailabilityRequestKey(
            sharedBoardingService.id,
            petIds,
            startDate,
            endDate
        );

        dispatch({
            type: ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_START,
            payload: { requestKey }
        });

        try {
            const { data } = await ApiPublicClient.get<Array<DaycareAvailabilityDateDto>>(
                boardingAvailabilityUrl,
                {
                    params: {
                        marketplace_id: marketplace.data.id,
                        service_id: sharedBoardingService.id,
                        pet_ids: petIds.join(','),
                        start_date: startDate.format('YYYY-MM-DD'),
                        end_date: endDate.format('YYYY-MM-DD')
                    }
                }
            );

            const availability = data[0]
                ? convertDaycareAvailabilityDate(data[0], marketplace.data.timeZone)
                : buildEmptyBoardingAvailability(startDate);

            dispatch({
                type: ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_SUCCESS,
                payload: {
                    requestKey,
                    availability
                }
            });

            return availability;
        } catch (error) {
            dispatch({
                type: ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_FAILED,
                payload: { requestKey }
            });

            throw error;
        }
    };
};

export const getOnlineBookingDaycareAvailabilityThunk = (
    date: Moment
): ThunkAction<Promise<DaycareAvailabilityDate>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const state = getState();
        const { marketplace, onlineBooking } = state;
        const daycareBookingItems = onlineBooking.bookingItems.filter(isOnlineBookingDaycareItem);
        const sharedDaycareService = daycareBookingItems.find(item => item.service)?.service;
        const petIds = daycareBookingItems.map(item => item.pet.id).filter(Boolean);

        if (!sharedDaycareService || petIds.length === 0) {
            dispatch(clearOnlineBookingDaycareAvailability());

            throw new Error('Daycare availability requires a selected service and pets');
        }

        const dateKey = date.format('YYYY-MM-DD');
        const requestKey = createDaycareAvailabilityRequestKey(
            sharedDaycareService.id,
            petIds,
            date
        );

        dispatch({
            type: ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_START,
            payload: { dateKey, requestKey }
        });

        try {
            const { data } = await ApiPublicClient.get<Array<DaycareAvailabilityDateDto>>(
                daycareAvailabilityUrl,
                {
                    params: {
                        marketplace_id: marketplace.data.id,
                        service_id: sharedDaycareService.id,
                        pet_ids: petIds.join(','),
                        date: date.format('YYYY-MM-DD'),
                        slot_size: 30
                    }
                }
            );

            const availability = data[0]
                ? convertDaycareAvailabilityDate(data[0], marketplace.data.timeZone)
                : buildEmptyDaycareAvailability(date);

            dispatch({
                type: ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_SUCCESS,
                payload: {
                    dateKey,
                    requestKey,
                    availability
                }
            });

            return availability;
        } catch (error) {
            dispatch({
                type: ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_FAILED,
                payload: { dateKey, requestKey }
            });

            throw error;
        }
    };
};

export const getOnlineBookingDaycareAvailabilityForDatesThunk = (
    dates: Array<Moment>
): ThunkAction<Promise<void>, RootState, null, RootAction> => {
    return async dispatch => {
        await Promise.all(
            dates.map(date =>
                dispatch(getOnlineBookingDaycareAvailabilityThunk(date.clone())).catch(
                    () => undefined
                )
            )
        );
    };
};

export const getOnlineBookingGroomingStaffNextAvailableDateThunk = (
    startDate: Moment,
    weeksCount: number,
    staffIds?: Array<number>
): ThunkAction<
    Promise<{
        availableStaffIds: Array<number>;
        unavailableStaffIds: Array<number>;
        nextDate: GroomingAvailableDate;
    }>,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        try {
            const { onlineBooking } = getState();

            const groomingBookingItems = onlineBooking.bookingItems.filter(
                isOnlineBookingGroomingItem
            );
            const petsCount = groomingBookingItems.length;

            const durationInMinutes = groomingBookingItems.reduce((durationSum, bookingItem) => {
                if (!bookingItem.service) {
                    return durationSum;
                }

                const serviceDurationForPet = getServiceDurationByPet(
                    bookingItem.service,
                    bookingItem.pet.type,
                    bookingItem.pet.size
                );

                if (!serviceDurationForPet) {
                    return durationSum;
                }

                return (durationSum += serviceDurationForPet);
            }, 0);

            const nextDate = await dispatch(
                getStaffNextAvailableDateThunk(
                    startDate,
                    petsCount,
                    durationInMinutes,
                    weeksCount,
                    staffIds
                )
            );

            const availableStaffIds = Array.from(
                new Set(nextDate.hours.flatMap(hour => hour.staffAvailable))
            );
            const unavailableStaffIds = Array.from(
                new Set(nextDate.hours.flatMap(hour => hour.staffUnavailable))
            );

            return {
                availableStaffIds,
                unavailableStaffIds,
                nextDate
            };
        } catch (error) {
            dispatch({
                type: ONLINE_BOOKING_ERROR
            });

            throw error;
        }
    };
};

export const getOnlineBookingGroomingStaffAvailabilityThunk = (
    date: Moment
): ThunkAction<Promise<GroomingAvailableDate>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_START
            });

            const { staffs, onlineBooking } = getState();

            const groomingBookingItems = onlineBooking.bookingItems.filter(
                isOnlineBookingGroomingItem
            );

            const selectedStaffId = groomingBookingItems[0].staff?.id;

            let staffIds = staffs.staffs
                .filter(staff => {
                    return groomingBookingItems.every(bookingItem => {
                        return bookingItem.service?.staffIds?.includes(staff.id);
                    });
                })
                .map(staff => staff.id);

            if (selectedStaffId !== undefined) {
                staffIds = [selectedStaffId];
            }

            const durationInMinutes = groomingBookingItems.reduce((durationSum, bookingItem) => {
                if (!bookingItem.service) {
                    return durationSum;
                }

                const serviceDurationForPet = getServiceDurationByPet(
                    bookingItem.service,
                    bookingItem.pet.type,
                    bookingItem.pet.size
                );

                if (!serviceDurationForPet) {
                    return durationSum;
                }

                return (durationSum += serviceDurationForPet);
            }, 0);

            const availability = await dispatch(
                fetchStaffsAvailabilityThunk(
                    date,
                    staffIds,
                    groomingBookingItems.length,
                    durationInMinutes
                )
            );

            const availabilityDate = availability[0];

            dispatch({
                type: ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_SUCCESS,
                payload: {
                    availability: availabilityDate
                }
            });

            return availabilityDate;
        } catch (error) {
            dispatch({
                type: ONLINE_BOOKING_ERROR
            });

            throw error;
        }
    };
};
